import { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Values mirror src/design/tokens.css so older pages match the RandCards design system.
export const BRAND = {
  background: '#000000',
  surface: '#181818',
  surfaceRaised: '#1F1F1F',
  border: '#272727',
  accent: '#E42545',
  accentSoft: 'rgba(228, 37, 69, 0.14)',
  text: '#FFFFFF',
  textMuted: '#666666',
  textSoft: '#9B9B9B',
  success: '#37BE7A',
  warning: '#E6A433',
  error: '#E42545',
  info: '#9B9B9B',
};

export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

export const APP_THEME = {
  colorScheme: 'dark',
  primaryColor: 'brand',
  colors: {
    brand: [
      '#FCE9EC',
      '#F7C3CC',
      '#F09AA9',
      '#EA6F85',
      '#E64A63',
      '#E42545',
      '#C91F3B',
      '#A81A31',
      '#861527',
      '#64101D',
    ],
  },
  palette: {
    mode: 'dark',
    primary: {
      main: BRAND.accent,
    },
    background: {
      default: BRAND.background,
      paper: BRAND.surface,
    },
    text: {
      primary: BRAND.text,
      secondary: BRAND.textSoft,
    },
    divider: BRAND.border,
    error: {
      main: BRAND.error,
    },
    warning: {
      main: BRAND.warning,
    },
    success: {
      main: BRAND.success,
    },
    info: {
      main: BRAND.info,
    },
  },
  breakpoints: {
    values: BREAKPOINTS,
    up: (key) => `(min-width:${BREAKPOINTS[key] ?? BREAKPOINTS.md}px)`,
    down: (key) => `(max-width:${(BREAKPOINTS[key] ?? BREAKPOINTS.md) - 0.05}px)`,
  },
  spacing: (value = 1) => `${value * 8}px`,
  radius: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
  },
};

const ThemeContext = createContext(APP_THEME);

export function ThemeContextProvider({ theme = APP_THEME, children }) {
  const value = useMemo(() => ({ ...APP_THEME, ...theme }), [theme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useAppTheme() {
  return useContext(ThemeContext);
}

export function alpha(input, opacity = 1) {
  if (!input) return `rgba(255, 255, 255, ${opacity})`;

  if (String(input).startsWith('rgba')) {
    return String(input).replace(/rgba\(([^)]+),[^)]+\)/, `rgba($1, ${opacity})`);
  }

  if (String(input).startsWith('rgb(')) {
    return String(input).replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }

  let color = String(input).replace('#', '').trim();
  if (color.length === 3) {
    color = color.split('').map((piece) => `${piece}${piece}`).join('');
  }

  if (color.length !== 6) {
    return input;
  }

  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}


// The design system allows only these values (redesign skill, Design Values).
const SPACE_STEPS = [0, 2, 4, 8, 12, 16, 24, 32, 40, 48, 64, 80, 96];
const RADIUS_STEPS = [0, 2, 4, 6, 8, 12, 16, 24];
const FONT_STEPS = [12, 14, 16, 18, 20, 24, 30, 36, 48, 60, 72, 96, 128];

function toPx(value) {
  if (typeof value === 'number') return value;
  const match = /^(-?\d*\.?\d+)(px|rem)?$/.exec(String(value).trim());
  if (!match) return null;
  return match[2] === 'rem' ? Number(match[1]) * 16 : Number(match[1]);
}

export function snapSpace(px) {
  const sign = px < 0 ? -1 : 1;
  const abs = Math.abs(px);
  if (abs > 96) return px;
  let best = SPACE_STEPS[0];
  for (const step of SPACE_STEPS) if (Math.abs(step - abs) < Math.abs(best - abs) || (Math.abs(step - abs) === Math.abs(best - abs) && step > best)) best = step;
  return sign * best;
}

export function snapRadius(px) {
  if (px >= 999) return 9999;
  if (px > 24) return px >= 48 ? 9999 : 24;
  let best = RADIUS_STEPS[0];
  for (const step of RADIUS_STEPS) if (Math.abs(step - px) <= Math.abs(best - px)) best = step;
  return best;
}

// Snap to the closest step below, never smaller than 12px.
export function snapFont(px) {
  let best = FONT_STEPS[0];
  for (const step of FONT_STEPS) if (step <= px + 0.01) best = step;
  return best;
}

function getWindowWidth() {
  if (typeof window === 'undefined') return BREAKPOINTS.lg;
  return window.innerWidth || BREAKPOINTS.lg;
}

function isResponsiveObject(value) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).some((key) => Object.prototype.hasOwnProperty.call(BREAKPOINTS, key))
  );
}

export function resolveResponsiveValue(value) {
  if (!isResponsiveObject(value)) {
    return value;
  }

  const width = getWindowWidth();
  const entries = Object.entries(BREAKPOINTS)
    .filter(([key]) => value[key] !== undefined)
    .sort((a, b) => a[1] - b[1]);

  let resolved = entries[0]?.length ? value[entries[0][0]] : undefined;
  entries.forEach(([key, minWidth]) => {
    if (width >= minWidth) {
      resolved = value[key];
    }
  });
  return resolved;
}

function mapToken(value) {
  if (typeof value !== 'string') return value;

  const tokenMap = {
    'primary.main': BRAND.accent,
    'background.default': BRAND.background,
    'background.paper': BRAND.surface,
    'text.primary': BRAND.text,
    'text.secondary': BRAND.textSoft,
    'error.main': BRAND.error,
    'warning.main': BRAND.warning,
    'success.main': BRAND.success,
    'info.main': BRAND.info,
    divider: BRAND.border,
  };

  return tokenMap[value] ?? value;
}

function spacingValue(value) {
  if (typeof value === 'number') return `${snapSpace(value * 8)}px`;
  const px = toPx(value);
  if (px !== null && /px|rem/.test(String(value))) return `${snapSpace(px)}px`;
  return mapToken(value);
}

function radiusValue(value) {
  if (typeof value === 'number') return `${snapRadius(Math.max(value, 1) * 4)}px`;
  const px = toPx(value);
  if (px !== null && /px|rem/.test(String(value))) return `${snapRadius(px)}px`;
  return mapToken(value);
}

function toCssValue(value) {
  if (typeof value === 'number') return `${value}px`;
  return mapToken(value);
}

function fontSizeValue(value) {
  const px = toPx(value);
  if (px !== null && (typeof value === 'number' || /px|rem/.test(String(value)))) return `${snapFont(px)}px`;
  return mapToken(value);
}

const SPACING_SHORTHANDS = {
  p: ['padding'],
  px: ['paddingLeft', 'paddingRight'],
  py: ['paddingTop', 'paddingBottom'],
  pt: ['paddingTop'],
  pr: ['paddingRight'],
  pb: ['paddingBottom'],
  pl: ['paddingLeft'],
  m: ['margin'],
  mx: ['marginLeft', 'marginRight'],
  my: ['marginTop', 'marginBottom'],
  mt: ['marginTop'],
  mr: ['marginRight'],
  mb: ['marginBottom'],
  ml: ['marginLeft'],
};

export function resolveSx(input, theme = APP_THEME) {
  if (!input) return {};

  if (Array.isArray(input)) {
    return input.reduce((acc, item) => ({ ...acc, ...resolveSx(item, theme) }), {});
  }

  if (typeof input === 'function') {
    return resolveSx(input(theme), theme);
  }

  if (typeof input !== 'object') {
    return {};
  }

  const style = {};

  Object.entries(input).forEach(([rawKey, rawValue]) => {
    const key = rawKey;
    const value = resolveResponsiveValue(rawValue);

    if (value === undefined || value === null) return;
    if (key.startsWith('&') || key.startsWith('@') || key.includes('.Mui') || key.includes('::')) {
      return;
    }

    if (SPACING_SHORTHANDS[key]) {
      SPACING_SHORTHANDS[key].forEach((prop) => {
        style[prop] = spacingValue(value);
      });
      return;
    }

    if (key === 'bgcolor') {
      style.backgroundColor = mapToken(value);
      return;
    }

    if (key === 'borderRadius') {
      style.borderRadius = radiusValue(value);
      return;
    }

    if (key === 'gap' || key === 'rowGap' || key === 'columnGap') {
      style[key] = spacingValue(value);
      return;
    }

    if (
      key === 'minWidth' ||
      key === 'maxWidth' ||
      key === 'width' ||
      key === 'minHeight' ||
      key === 'maxHeight' ||
      key === 'height' ||
      key === 'top' ||
      key === 'left' ||
      key === 'right' ||
      key === 'bottom'
    ) {
      style[key] = toCssValue(value);
      return;
    }

    if (key === 'fontSize') {
      style.fontSize = fontSizeValue(value);
      return;
    }

    if (key === 'fontWeight') {
      const weight = Number(value);
      style.fontWeight = Number.isFinite(weight) && weight > 700 ? 700 : value;
      return;
    }

    if (key === 'fontStyle' && value === 'italic') {
      style.fontStyle = 'normal';
      return;
    }

    if (key === 'textTransform' && value === 'uppercase') {
      return;
    }

    style[key] = mapToken(value);
  });

  return style;
}

export function mergeStyles(...styles) {
  return Object.assign({}, ...styles.filter(Boolean));
}

export function useMediaQuery(queryInput) {
  const query = resolveResponsiveValue(queryInput) || queryInput;

  const getMatches = () => {
    if (typeof window === 'undefined' || !window.matchMedia || !query) return false;
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia || !query) return undefined;

    const mediaQuery = window.matchMedia(query);
    const listener = () => setMatches(mediaQuery.matches);

    listener();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }

    mediaQuery.addListener(listener);
    return () => mediaQuery.removeListener(listener);
  }, [query]);

  return matches;
}

// Base styles now live in src/design (tokens.css, base.css, components.css), loaded by App.js.
export function GlobalStyles() {
  return null;
}
